import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import React from "react";
import {
  GroupShape,
  IconShape,
  Shape,
  SpotShape,
  ZoneShape,
  ZoneTypeDefinition
} from "../../../../types";
import { isOrganisationZoneId } from "../../../../utils/zones";
import { ColorPickerInput } from "../../../ui/ColorPickerInput";
import { EditorField } from "../../../ui/EditorField";
import { EditorSelect } from "../../../ui/EditorSelect";
import { EditorSectionLabel, PanelSection } from "../../../ui/PanelShell";
import { useMapCanvas } from "../../contexts/MapCanvasContext";

/** Count zones + icons anywhere on the map referencing a category. */
const countCategoryUsage = (shapes: Shape[], categoryId: string): number => {
  let count = 0;
  const walk = (list: Shape[]) => {
    for (const item of list) {
      if (
        (item.type === "zone" || item.type === "icon") &&
        (item as ZoneShape | IconShape).categoryTypeId === categoryId
      ) {
        count++;
      }
      if (item.type === "group") {
        walk((item as GroupShape).children || []);
      } else if (item.type === "spot") {
        walk(((item as SpotShape).companions as Shape[]) || []);
      }
    }
  };
  walk(shapes);
  return count;
};

export const NO_CATEGORY_VALUE = "__none__";

export interface CategoryTypesSectionProps {
  /** The selected shape's category id (undefined = none). */
  selectedCategoryId?: string;
  /** Assign / clear the category on the selected shape(s). */
  onChangeCategory: (id: string | undefined) => void;
  /** Clear references on selected shape(s) when a category is deleted. */
  onCategoryDeleted?: (id: string) => void;
  /** Optional helper line under the select. */
  hint?: string;
  namePlaceholder?: string;
}

/**
 * "Category" section for the icon inspector: pick a map-local category for
 * the selection (points of interest such as Restrooms), manage the list
 * inline (rename-by-recreate, per-row color editing, delete) and add new
 * ones. Categories live on the map value (`zoneTypes`); entries cached
 * there for organisation zones are managed in Settings and hidden here.
 */
export const CategoryTypesSection: React.FC<CategoryTypesSectionProps> = ({
  selectedCategoryId,
  onChangeCategory,
  onCategoryDeleted,
  hint,
  namePlaceholder = "Category name (e.g. Meeting Room)"
}) => {
  const { zoneTypes: allZoneTypes, setZoneTypes, shapesRef } = useMapCanvas();
  const zoneTypes = React.useMemo(
    () => allZoneTypes.filter((t) => !isOrganisationZoneId(t.id)),
    [allZoneTypes]
  );

  // Inline "add new type" state — kept local so toggling the form doesn't
  // pollute the saved map until the user commits a new type.
  const [isAdding, setIsAdding] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [newColor, setNewColor] = React.useState("#3B82F6");
  // Delete-confirmation state for categories still referenced on the map.
  const [pendingDelete, setPendingDelete] = React.useState<{
    type: ZoneTypeDefinition;
    usageCount: number;
  } | null>(null);

  const commitNewType = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `zone-type-${Date.now()}`;
    const next: ZoneTypeDefinition = { id, name: trimmed, color: newColor };
    setZoneTypes((prev) => [...prev, next]);
    // Auto-assign the new type so the user sees it land immediately.
    onChangeCategory(id);
    setNewName("");
    setNewColor("#3B82F6");
    setIsAdding(false);
  };

  const removeType = (id: string) => {
    setZoneTypes((prev) => prev.filter((t) => t.id !== id));
    onCategoryDeleted?.(id);
  };

  const requestRemoveType = (type: ZoneTypeDefinition) => {
    const usageCount = countCategoryUsage(shapesRef.current || [], type.id);
    if (usageCount > 0) {
      setPendingDelete({ type, usageCount });
      return;
    }
    removeType(type.id);
  };

  const updateTypeColor = (id: string, color: string) => {
    setZoneTypes((prev) =>
      prev.map((t) => (t.id === id ? { ...t, color } : t))
    );
  };

  return (
    <PanelSection>
      <div className="flex items-baseline justify-between">
        <EditorSectionLabel>Category</EditorSectionLabel>
        <button
          type="button"
          className="text-blue-600 text-xs hover:text-blue-700"
          onClick={() => setIsAdding((v) => !v)}
        >
          {isAdding ? "Cancel" : "+ New"}
        </button>
      </div>
      <div className="flex flex-col gap-1">
        <EditorSelect
          label="Category"
          value={selectedCategoryId || NO_CATEGORY_VALUE}
          onValueChange={(value) =>
            onChangeCategory(value === NO_CATEGORY_VALUE ? undefined : value)
          }
          options={[
            { value: NO_CATEGORY_VALUE, label: "(none)" },
            ...zoneTypes.map((t) => ({ value: t.id, label: t.name }))
          ]}
        />
        {hint ? <p className="text-[11px] text-neutral-400">{hint}</p> : null}
        {/* Per-category row: editable color swatch + name + delete. */}
        {zoneTypes.length > 0 && (
          <div className="mt-1 flex flex-col gap-1">
            {zoneTypes.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-2 text-neutral-600 text-xs"
              >
                {/* The plain square IS the picker trigger — click, pick,
                    saved. No inline hex input in the compact list. */}
                <ColorPickerInput
                  value={t.color}
                  onChange={(color) => updateTypeColor(t.id, color)}
                  showInput={false}
                  trigger={
                    <button
                      type="button"
                      title="Change color"
                      aria-label={`Change ${t.name} color`}
                      className="inline-block h-3 w-3 shrink-0 rounded-sm border border-neutral-300"
                      style={{ background: t.color }}
                    />
                  }
                />
                <span className="flex-1 truncate">{t.name}</span>
                <button
                  type="button"
                  className="text-neutral-400 hover:text-red-600"
                  onClick={() => requestRemoveType(t)}
                  title="Delete category"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        {isAdding && (
          <div className="mt-1 flex flex-col gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-2">
            <EditorField
              label="Category name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={namePlaceholder}
            />
            <div className="flex items-center gap-2">
              <ColorPickerInput value={newColor} onChange={setNewColor} />
              <button
                type="button"
                className="ml-auto flex h-7 items-center justify-center rounded-md bg-blue-600 px-3 font-medium text-white text-xs shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-neutral-300"
                style={{
                  backgroundColor: newName.trim() ? "#2563eb" : undefined
                }}
                onClick={commitNewType}
                disabled={!newName.trim()}
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete) removeType(pendingDelete.type.id);
          setPendingDelete(null);
        }}
        title="Delete category?"
        confirmLabel="Delete"
        message={
          <>
            <strong>{pendingDelete?.type.name}</strong> is used by{" "}
            {pendingDelete?.usageCount}{" "}
            {pendingDelete?.usageCount === 1 ? "item" : "items"} on this map
            (zones or icons). Deleting it leaves them without a category.
          </>
        }
      />
    </PanelSection>
  );
};
